import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {NbThemeService} from '@nebular/theme';
import {SpatialCardVm} from '@theme/components/card-components/spatial-card/spatial-card.vm';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {debounceTime, map, takeUntil} from 'rxjs/operators';
import {GeospatialCenterCoordinate} from '../../../../../pages/geospatial-viewer/model/viewer-config';
import {GenerateLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/generate-layer.service';
import {User} from '@core/interfaces/common/users';
import {UsersStore} from '@store/common/users.store';
import {ProjectDto} from '@core/interfaces/engin/program-management/project';
import {SpatialCardBaseComponent} from '@theme/components';
import {
    SpatialCardConfigProject,
    SpatialCardDataProject,
    SpatialCardResponseProject,
} from '@theme/components/card-components/spatial-card/model/model-spatial-card';
import {VisualizationColorCode} from '../../../../../pages/geospatial-viewer/model/visualization';
import {BucketDto} from '../../../../../pages/geospatial-viewer/layers/metric-layer';
import {FormBuilder, FormGroup} from '@angular/forms';
import {Filter, FilterFieldType, FilterOperatorType} from '@core/interfaces/system/system-common';
import {GeospatialColourType} from '../../../../../pages/geospatial-viewer/model/spatial-colours';

@Component({
    selector: 'ngx-spatial-card-projects',
    templateUrl: './spatial-card-projects.component.html',
    styleUrls: [
        './spatial-card-projects.component.scss',
        '../spatial-card.component.scss',
        '../../../../styles/esri-overrides.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpatialCardProjectsComponent extends SpatialCardBaseComponent<SpatialCardConfigProject> implements OnInit {
    // Override: default map title
    @Input() title: string = 'Geospatial overview: projects';
    // Map data source: map data (i.e., assets), initial location
    @Input() dataSource$: Observable<SpatialCardResponseProject> = new BehaviorSubject(null);
    // Support for filtering projects by ownership
    @Input() ownershipFilter?: BehaviorSubject<Filter>;

    public ownershipForm: FormGroup;
    // Data store: map, legend settings
    private cardResponse: BehaviorSubject<SpatialCardResponseProject> = new BehaviorSubject(null);
    private activeProjectsLegend: string[] = [];

    ANYONE_ELSE: string = 'By anyone else';
    BY_ME: string = 'By me';

    private currentUser: User;

    constructor(
        public nbThemeService: NbThemeService,
        public vm: SpatialCardVm,
        public generateLayerService: GenerateLayerService,
        private userStore: UsersStore,
        private fb: FormBuilder,
    ) {
        super(nbThemeService, vm, generateLayerService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.layerIdAlias = 'project';
        if (!!this.ownershipFilter) {
            this.initForm();
        }

        this.userStore.currentUser$.pipe(takeUntil(this.unsubscribe$)).subscribe((currentUser: User) => {
            this.currentUser = currentUser;
        });

        this.initMapServices();
    }

    /*
     * Implement abstract methods
     */
    public subscribeToMapChanges(): void {
        combineLatest([this.dataSource$, this.nbThemeService.onThemeChange()])
            .pipe(
                takeUntil(this.unsubscribe$),
                map(([resp, themeChange]: [SpatialCardResponseProject, any]) => {
                    this.cardResponse.next(resp);

                    if (!this.mapInitializedFlag.value) {
                        super.initializeMapSequence(resp.configData);
                    }
                    this.activeProjectsLegend.push(this.BY_ME, this.ANYONE_ELSE);
                    this.updateProjectLayer(resp.sourceData);
                }),
            )
            .subscribe();
    }

    public onSelectMapFeature(firstGraphic: any, _: any[]): void {}

    public onMapViewportChange(newCoordinate: GeospatialCenterCoordinate): void {
        const config: SpatialCardConfigProject = this.cardResponse.getValue().configData;
        const newConfig: SpatialCardConfigProject = {
            ...config,
            centerCoordinate: {
                ...config.centerCoordinate,
                ...newCoordinate,
            },
        };
        const isEquivalent: boolean = this.vm.isEquivalent(config.centerCoordinate, newConfig.centerCoordinate);

        if (!isEquivalent) {
            this.onMapLocationChange.emit(newConfig);
        }
    }

    /*
     * Custom methods
     */
    /** Initialize map services: legend */
    private initMapServices(): void {
        // TODO: the project layer clearly does not work with the main map legend right now since it has no colours.
        this.legendBuckets.next([
            {
                key: 'me',
                labelList: [this.BY_ME],
                colour: GeospatialColourType.BLUE,
            },
            {
                key: 'else',
                labelList: [this.ANYONE_ELSE],
                colour: GeospatialColourType.GREY,
            },
        ] as BucketDto[]);
    }

    /** Helper for rendering the map layer(s). Remove existing layers, generate and add new project layer. */
    private updateProjectLayer(mapDataSource: SpatialCardDataProject): void {
        super.removeMapLayers();

        const projects: ProjectDto[] = mapDataSource.projects;

        // Create new layer and add to map
        if (projects.length > 0) {
            const newLayer = this.generateLayerService.createProjectLayer(
                projects,
                this.currentUser.id,
                VisualizationColorCode.OWNERSHIP,
            );

            if (!newLayer) {
                return;
            }

            this.map.add(newLayer);
        }
    }

    /** Legend selection needs to toggle visibility of projects */
    public onUpdateProjects(event: {owner: string; value: boolean}): void {
        const allProjects: ProjectDto[] = this.cardResponse.getValue()?.sourceData.projects;
        let projectsActive: ProjectDto[] = [];

        [this.BY_ME, this.ANYONE_ELSE].forEach((legendCategory: string) => {
            if (event.owner.includes(legendCategory) || this.activeProjectsLegend.includes(legendCategory)) {
                if (!event.value && event.owner.includes(legendCategory)) {
                    this.activeProjectsLegend = this.activeProjectsLegend.filter(
                        (str: string) => str !== legendCategory,
                    );
                } else {
                    if (legendCategory === this.BY_ME) {
                        projectsActive = [
                            ...allProjects.filter((p: ProjectDto) => p.ownerId === this.currentUser.id),
                            ...projectsActive,
                        ];
                    } else {
                        projectsActive = [
                            ...allProjects.filter((p: ProjectDto) => p.ownerId !== this.currentUser.id),
                            ...projectsActive,
                        ];
                    }

                    if (!this.activeProjectsLegend.includes(legendCategory)) {
                        this.activeProjectsLegend.push(legendCategory);
                    }
                }
            }
        });

        this.updateProjectLayer({projects: projectsActive} as SpatialCardDataProject);
    }

    /** Initialize filtering by project ownership */
    private initForm(): void {
        this.ownershipForm = this.fb.group({
            categoriesSelected: [null],
        });

        this.ownershipForm
            .get('categoriesSelected')
            .valueChanges.pipe(
                debounceTime(500),
                map((selected: BucketDto[]) => {
                    if (!selected || selected.length !== 1) return null;
                    return {
                        fieldKey: 'ownerId',
                        operator: selected[0].key === 'me' ? FilterOperatorType.EQUAL : FilterOperatorType.NOT_EQUAL,
                        value: this.userStore.getCurrentUser()?.id,
                        fieldType: FilterFieldType.STRING,
                    } as Filter;
                }),
                takeUntil(this.unsubscribe$),
            )
            .subscribe((filter: Filter) => this.ownershipFilter.next(filter));
    }
}
